<script setup lang="ts">
/**
 * The page toolbar row (spec §5): functions only — search, filters, sort, view
 * switches, date navigation, counters — with the page's primary action as an
 * icon button at the right end. It never carries a title: the sidebar says
 * where you are. A page with no functions renders no toolbar at all.
 *
 * It pins to the top of the scrolling content and the content runs under a
 * ~22 px fade from the panel colour — shown only once content has actually
 * scrolled under the row (issue 1040), never at rest; a selection bar (`#selection`) appears as
 * a second row beneath it and pins with it. Inside AppShell's padded content
 * (or a padded ScrollRegion) the row bleeds to the panel edges through
 * `--lumi-content-pad`, so the fade spans the full width.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    sticky?: boolean;
    /**
     * A toolbar whose left side holds an identity block (the Person page's avatar + name):
     * the right-hand actions drop under it when the row gets too narrow for both, instead of
     * riding over it.
     */
    wrap?: boolean;
    /**
     * A toolbar whose only function is a segment control over a form-width page (Billing):
     * the control sits centred over the column below it, hugging its tabs.
     */
    center?: boolean;
  }>(),
  { sticky: true, wrap: false, center: false },
);

// Issue 1040: the fade is a cue that something is hidden under the row, so it
// shows only when it is true — the scroll container has scrolled AND the row is
// pinned at its top. A toolbar further down a page (under a banner) is not pinned
// until the content above it has scrolled away, so `scrollTop > 0` alone would
// light the fade over content that is still fully in view.
const root = ref<HTMLElement | null>(null);
const scrolled = ref(false);
let scroller: HTMLElement | null = null;

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

function update() {
  const el = root.value;
  if (!el || !scroller || !props.sticky) {
    scrolled.value = false;
    return;
  }
  if (scroller.scrollTop <= 0) {
    scrolled.value = false;
    return;
  }
  // Pinned: the row's top sits at the scroll port's top edge (it bleeds over the
  // container's padding, see `.lumi-toolbar--sticky`). One pixel of slack for
  // fractional zoom.
  const portTop = scroller.getBoundingClientRect().top + scroller.clientTop;
  scrolled.value = el.getBoundingClientRect().top <= portTop + 1;
}

onMounted(() => {
  if (!root.value) return;
  scroller = scrollParent(root.value);
  scroller?.addEventListener('scroll', update, { passive: true });
  update();
});
onBeforeUnmount(() => scroller?.removeEventListener('scroll', update));
</script>

<template>
  <div ref="root" class="lumi-toolbar" :class="{ 'lumi-toolbar--scrolled': scrolled, 'lumi-toolbar--sticky': sticky, 'lumi-toolbar--wrap': wrap, 'lumi-toolbar--center': center }">
    <div class="lumi-toolbar__row">
      <div class="lumi-toolbar__left"><slot /></div>
      <div v-if="$slots.actions" class="lumi-toolbar__right"><slot name="actions" /></div>
    </div>
    <div v-if="$slots.selection" class="lumi-toolbar__selection"><slot name="selection" /></div>
  </div>
</template>

<style>
/* Horizontal gutter is `max(--lumi-content-pad, --space-4)`, not the pad alone
   (#468): a host that renders this toolbar in-page, straight into an UNPADDED
   AppShell/ScrollRegion (backoffice-ui's boards), inherits `--lumi-content-pad: 0`
   from the root token default, and the toolbar's search field and right-most
   button sat flush against the panel edge with nothing to stop them. `max()` only
   ever RAISES the padding to the content gutter floor; a host with real ambient
   padding (>= --space-4) is unaffected. Every UI renders the toolbar in the page
   itself — there is no header outlet to teleport into and no per-host rule that
   zeroes it. The BLEED margin stays pad-only: it cancels exactly the ambient
   padding there is to cancel, so the fade still spans the true panel width instead
   of stopping at this floor. */
.lumi-toolbar{position:relative;background:var(--shell-panel);margin:calc(-1 * var(--lumi-content-pad,0px)) calc(-1 * var(--lumi-content-pad,0px)) var(--space-3);padding:12px max(var(--lumi-content-pad,0px), var(--space-4)) 0;font-family:var(--font-body);}
/* One height for every control in the bar (#705): buttons already stand at --control-h, and
   fields (search, select, combobox, date) read --field-h, so the bar hands them the same value
   instead of letting a 40 px field sit beside 36 px buttons. Panels teleport out of the bar and
   keep the form-field height. */
.lumi-toolbar{--field-h:var(--control-h);}
/* `top` carries the bleed too: a sticky box measures its inset from the scroll
   container's CONTENT edge, so with `top:0` a toolbar in padded content parks one
   pad below the panel edge (and rows scroll visibly above it). */
.lumi-toolbar--sticky{position:sticky;top:calc(-1 * var(--lumi-content-pad,0px));z-index:var(--layer-sticky);}
/* The fade under the toolbar (spec §5) — content scrolls under it. Hidden at rest
   (issue 1040): it appears only once content has scrolled under the row. */
.lumi-toolbar::after{content:'';position:absolute;left:0;right:0;top:100%;height:var(--fade-h);background:linear-gradient(to bottom,var(--shell-panel),transparent);pointer-events:none;opacity:0;transition:opacity var(--dur-fast) var(--ease-standard);}
.lumi-toolbar--scrolled::after{opacity:1;}
.lumi-toolbar__row{display:flex;align-items:center;gap:var(--space-2);min-height:var(--control-h);padding-bottom:10px;}
.lumi-toolbar__left{display:flex;align-items:center;gap:var(--space-2);flex:1;min-width:0;flex-wrap:wrap;}
.lumi-toolbar--wrap .lumi-toolbar__row{flex-wrap:wrap;}
.lumi-toolbar--wrap .lumi-toolbar__left{flex:1 1 300px;flex-wrap:nowrap;}
.lumi-toolbar--center .lumi-toolbar__left{justify-content:center;}
.lumi-toolbar__right{display:flex;align-items:center;gap:var(--space-2);flex-shrink:0;margin-left:auto;}
.lumi-toolbar__selection{display:flex;align-items:center;gap:var(--space-3);flex-wrap:wrap;min-height:var(--control-h);padding-bottom:10px;font-size:var(--text-sm);color:var(--text-body);}
</style>
